import { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "../../redux/configureStore";
import OT from "@opentok/client";
import usePublishCamera from "./presenting/UsePublishCamera";
import usePublishScreenShare from "./presenting/UsePublishScreenShare";
import { addPresenter, removePresenter } from "./sessionSlice";
import { VenueConnectionData, VenueSessionState } from "../../app/types";
import { useSession } from "../api/helpers";

const useStreams = (
  vonageSession: OT.Session,
  sessionState: VenueSessionState,
) => {
  const dispatch = useAppDispatch();

  const session = useSession();
  const [streams, setStreams] = useState<OT.Stream[]>([]);

  const addStream = useCallback(
    (streamToAdd: OT.Stream) => {
      const connectionData = JSON.parse(
        streamToAdd.connection.data,
      ) as VenueConnectionData;
      if (session?.sessionId) {
        dispatch(
          addPresenter(
            session.sessionId,
            connectionData,
            streamToAdd.streamId,
            streamToAdd.videoType,
          ),
        );
      }

      setStreams((oldStreams) =>
        [...oldStreams, streamToAdd].sort((a, b) =>
          a.streamId.localeCompare(b.streamId),
        ),
      );
    },
    [dispatch, session?.sessionId],
  );

  const removeStream = useCallback(
    (streamToRemove: OT.Stream) => {
      if (session?.sessionId) {
        dispatch(
          removePresenter(
            session.sessionId,
            streamToRemove.streamId,
            streamToRemove.videoType,
          ),
        );
      }
      setStreams((oldStreams) =>
        [...oldStreams].filter(
          (stream) => stream.streamId !== streamToRemove.streamId,
        ),
      );
    },
    [dispatch, session?.sessionId],
  );

  useEffect(() => {
    const onDisconnect = () => {
      setStreams([]);
    };
    if (vonageSession) {
      vonageSession.on("sessionDisconnected", onDisconnect);
    }

    return () => vonageSession?.off("sessionDisconnected", onDisconnect);
  }, [vonageSession]);

  useEffect(() => {
    if (vonageSession !== undefined) {
      vonageSession.on("streamCreated", (event) => addStream(event.stream));
      vonageSession.on("streamDestroyed", (event) =>
        removeStream(event.stream),
      );
    }

    return () => {
      vonageSession?.off("streamCreated");
      vonageSession?.off("streamDestroyed");
    };
  }, [addStream, removeStream, vonageSession]);

  usePublishCamera({
    vonageSession,
    sessionState,
    addStream,
    removeStream,
  });

  usePublishScreenShare({
    vonageSession,
    addStream,
    removeStream,
    sessionState,
  });

  return streams;
};

export default useStreams;
